import { atom } from 'jotai'
import { languageState } from '@repo/unicity'

// Types
export interface LanguageItem {
    code: string
    locale: string
    name: string
}

export interface LanguageSettings {
    default: string
    items: LanguageItem[]
}

// Default supported languages for GLIC 2027
export const DEFAULT_LANGUAGES: LanguageItem[] = [
    { code: 'EN', locale: 'en', name: 'English' },
    { code: 'AR', locale: 'ar', name: 'العربية' },
    { code: 'DE', locale: 'de', name: 'Deutsch' },
    { code: 'ES', locale: 'es', name: 'Español' },
    { code: 'FR', locale: 'fr', name: 'Français' },
    { code: 'JP', locale: 'ja', name: '日本語' },
    { code: 'KM', locale: 'km', name: 'ភាសាខ្មែរ' },
    { code: 'KR', locale: 'ko', name: '한국어' },
    { code: 'TH', locale: 'th', name: 'ไทย' },
    { code: 'VN', locale: 'vi', name: 'Tiếng Việt' },
    { code: 'CH', locale: 'hk', name: '中文' }
]

// Atoms - initialize with default languages
export const availableLanguagesAtom = atom<LanguageItem[]>(DEFAULT_LANGUAGES)
export const selectedLanguageAtom = atom<string>('EN')
export const isLoadingLanguagesAtom = atom<boolean>(false)

// Derived: Get current language object
export const currentLanguageAtom = atom(get => {
    const languages = get(availableLanguagesAtom)
    const selectedCode = get(selectedLanguageAtom)
    return languages.find(lang => lang.code.toUpperCase() === selectedCode.toUpperCase()) || null
})

// Action: Set available languages from country API
export const setLanguagesFromCountryAtom = atom(null, (_get, set, settings: LanguageSettings) => {
    // Use API languages if provided, otherwise keep default
    if (settings.items && settings.items.length > 0) {
        set(availableLanguagesAtom, settings.items)
    }
    // Set default language
    const defaultLang = settings.default || settings.items?.[0]?.code || 'EN'
    set(selectedLanguageAtom, defaultLang.toUpperCase())
    // Also set unicity languageState
    set(languageState, defaultLang.toUpperCase())
})

// Action: Change language
export const changeLanguageAtom = atom(null, (_get, set, languageCode: string) => {
    const upperCode = languageCode.toUpperCase()
    set(selectedLanguageAtom, upperCode)
    // Also set unicity languageState for useTranslate to work
    set(languageState, upperCode)
    // Save to localStorage for persistence
    localStorage.setItem('glic2027_language', upperCode)
})

// Action: Restore language from localStorage
export const restoreLanguageAtom = atom(null, (get, set) => {
    const saved = localStorage.getItem('glic2027_language')
    if (saved) {
        const languages = get(availableLanguagesAtom)
        const savedUpper = saved.toUpperCase()
        const exists = languages.some(lang => lang.code.toUpperCase() === savedUpper)
        if (exists) {
            set(selectedLanguageAtom, savedUpper)
            // Also set unicity languageState
            set(languageState, savedUpper)
        }
    }
})
